(() => {
  'use strict';

  const STORAGE_KEY =
    'applianceBarnMultiBuyQuote';

  const EVENT_NAME =
    'multi-buy-quote:updated';


  /*
  ============================================================
  READ QUOTE
  ============================================================
  */

  function readQuote() {
    try {
      const savedQuote =
        localStorage.getItem(
          STORAGE_KEY
        );

      if (!savedQuote) {
        return [];
      }

      const parsedQuote =
        JSON.parse(
          savedQuote
        );

      return Array.isArray(
        parsedQuote
      )
        ? parsedQuote
        : [];

    } catch (error) {
      console.error(
        'Could not read the multi-buy quote.',
        error
      );

      return [];
    }
  }


  /*
  ============================================================
  SAVE QUOTE
  ============================================================
  */

  function saveQuote(items) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(items)
      );

      document.dispatchEvent(
        new CustomEvent(
          EVENT_NAME,
          {
            detail: {
              items,
              count:
                getQuoteCount(items)
            }
          }
        )
      );

    } catch (error) {
      console.error(
        'Could not save the multi-buy quote.',
        error
      );
    }
  }


  /*
  ============================================================
  QUOTE COUNT
  ============================================================
  */

  function getQuoteCount(
    items = readQuote()
  ) {
    return items.reduce(
      (total, item) => {
        return (
          total +
          Number(
            item.quantity || 1
          )
        );
      },
      0
    );
  }


  /*
  ============================================================
  NORMALISE VARIANT TITLE
  ============================================================
  */

  function normaliseVariantTitle(
    title
  ) {
    if (
      !title ||
      title === 'Default Title'
    ) {
      return '';
    }

    return title;
  }


  /*
  ============================================================
  BUILD ITEM FROM BUTTON
  ============================================================
  */

  function getButtonItem(
    button
  ) {
    return {
      variantId:
        String(
          button.dataset.variantId ||
          ''
        ),

      productId:
        String(
          button.dataset.productId ||
          ''
        ),

      title:
        button.dataset.productTitle ||
        '',

      variantTitle:
        normaliseVariantTitle(
          button.dataset.variantTitle
        ),

      sku:
        button.dataset.sku ||
        '',

      price:
        Number(
          button.dataset.price ||
          0
        ),

      url:
        button.dataset.url ||
        '',

      image:
        button.dataset.image ||
        '',

      quantity:
        1,

      priceRequest:
        button.dataset.priceRequest ===
        'true'
    };
  }


  /*
  ============================================================
  ADD ITEM
  ============================================================
  */

  function addItem(
    item
  ) {
    if (!item.variantId) {
      console.error(
        'Quote item is missing a variant ID.'
      );

      return;
    }

    const items =
      readQuote();


    const existingItem =
      items.find(
        quoteItem =>
          String(
            quoteItem.variantId
          ) ===
          String(
            item.variantId
          )
      );


    /*
    Same appliance added again:
    increase quantity.
    */

    if (existingItem) {
      existingItem.quantity =
        Number(
          existingItem.quantity ||
          1
        ) + 1;


      /*
      If it was ever added using
      Request Your Price, retain that.
      */

      if (item.priceRequest) {
        existingItem.priceRequest =
          true;
      }

    } else {
      items.push(item);
    }


    saveQuote(items);


    /*
    ==========================================================
    IMPORTANT:
    THIS IS THE ORIGINAL WORKING DRAWER OPEN BEHAVIOUR.
    ==========================================================
    */

    if (
      window.MultiBuyQuoteDrawer &&
      typeof
        window.MultiBuyQuoteDrawer.open ===
        'function'
    ) {
      window.MultiBuyQuoteDrawer.open();
    }
  }


  /*
  ============================================================
  UPDATE COUNTERS
  ============================================================
  */

  function updateCounters() {
    const count =
      getQuoteCount();


    document
      .querySelectorAll(
        '[data-quote-count]'
      )
      .forEach(
        counter => {
          counter.textContent =
            String(count);

          counter.hidden =
            count === 0;
        }
      );


    document
      .querySelectorAll(
        '[data-quote-link]'
      )
      .forEach(
        link => {
          link.setAttribute(
            'aria-label',
            `Multi-buy quote with ${count} ${
              count === 1
                ? 'item'
                : 'items'
            }`
          );
        }
      );
  }


  /*
  ============================================================
  BUTTON ADDED STATE
  ============================================================
  */

  function showAddedState(
    button
  ) {
    const originalText =
      button.dataset.originalText ||
      button.textContent.trim();


    button.dataset.originalText =
      originalText;


    if (
      button.dataset.priceRequest ===
      'true'
    ) {
      button.textContent =
        'Added to price request';

    } else {
      button.textContent =
        'Added to quote';
    }


    button.classList.add(
      'is-added'
    );


    window.setTimeout(
      () => {
        button.textContent =
          originalText;

        button.classList.remove(
          'is-added'
        );
      },
      1600
    );
  }


  /*
  ============================================================
  CREATE POA MULTI-BUY BUTTON
  ============================================================
  */

  function createPoaMultiBuyButton(
    actionsContainer
  ) {
    const existingButton =
      actionsContainer.querySelector(
        '[data-poa-multibuy]'
      );


    if (existingButton) {
      return;
    }


    const requestButton =
      actionsContainer.querySelector(
        '[data-price-request="true"]'
      );


    if (!requestButton) {
      return;
    }


    const button =
      document.createElement(
        'button'
      );


    button.type =
      'button';


    button.className =
      'appliance-card__button appliance-card__button--quote';


    button.textContent =
      'Add to multi-buy quote';


    button.setAttribute(
      'data-quote-add',
      ''
    );


    button.setAttribute(
      'data-poa-multibuy',
      ''
    );


    const attributes = [
      'variantId',
      'productId',
      'productTitle',
      'variantTitle',
      'sku',
      'price',
      'url',
      'image'
    ];


    attributes.forEach(
      attribute => {
        const value =
          requestButton.dataset[
            attribute
          ];


        if (
          value !== undefined
        ) {
          button.dataset[
            attribute
          ] =
            value;
        }
      }
    );


    requestButton.insertAdjacentElement(
      'afterend',
      button
    );
  }


  /*
  ============================================================
  SHOW / HIDE POA MULTI-BUY BUTTON
  ============================================================
  */

  function updatePoaMultiBuyButtons() {
    const items =
      readQuote();


    const hasQuote =
      items.length > 0;


    document
      .querySelectorAll(
        '[data-poa-actions]'
      )
      .forEach(
        actionsContainer => {

          const existingButton =
            actionsContainer.querySelector(
              '[data-poa-multibuy]'
            );


          /*
          No products selected:
          POA only shows Request Your Price.
          */

          if (!hasQuote) {
            if (existingButton) {
              existingButton.remove();
            }

            return;
          }


          /*
          Quote already started:
          POA can also be added to Multi-Buy.
          */

          if (!existingButton) {
            createPoaMultiBuyButton(
              actionsContainer
            );
          }

        }
      );
  }


  /*
  ============================================================
  REFRESH UI
  ============================================================
  */

  function refreshQuoteUI() {
    updateCounters();
    updatePoaMultiBuyButtons();
  }


  /*
  ============================================================
  PRODUCT BUTTON CLICKS
  ============================================================
  */

  document.addEventListener(
    'click',
    event => {

      const button =
        event.target.closest(
          '[data-quote-add]'
        );


      if (!button) {
        return;
      }


      event.preventDefault();
      event.stopPropagation();


      const item =
        getButtonItem(
          button
        );


      addItem(item);

      showAddedState(button);
    }
  );


  /*
  ============================================================
  QUOTE UPDATED
  ============================================================
  */

  document.addEventListener(
    EVENT_NAME,
    refreshQuoteUI
  );


  /*
  ============================================================
  PUBLIC API
  ============================================================
  */

  window.MultiBuyQuote = {
    read:
      readQuote,

    save:
      saveQuote,

    count:
      getQuoteCount,

    add:
      addItem,

    refresh:
      refreshQuoteUI,

    storageKey:
      STORAGE_KEY
  };


  /*
  ============================================================
  INITIAL REFRESH
  ============================================================
  */

  refreshQuoteUI();

})();



/*
============================================================
DRAWER HELPERS
============================================================
*/


function formatQuoteMoney(
  valueInPence
) {
  const amount =
    Number(
      valueInPence || 0
    ) / 100;


  return new Intl.NumberFormat(
    'en-GB',
    {
      style:
        'currency',

      currency:
        'GBP'
    }
  ).format(
    amount
  );
}



/*
============================================================
READ ITEMS FOR DRAWER
============================================================
*/


function getMultiBuyQuoteItems() {
  if (
    window.MultiBuyQuote &&
    typeof
      window.MultiBuyQuote.read ===
      'function'
  ) {
    return window.MultiBuyQuote.read();
  }


  try {
    const storedItems =
      localStorage.getItem(
        'applianceBarnMultiBuyQuote'
      );


    if (!storedItems) {
      return [];
    }


    const parsedItems =
      JSON.parse(
        storedItems
      );


    return Array.isArray(
      parsedItems
    )
      ? parsedItems
      : [];

  } catch (error) {
    console.error(
      'Unable to read Multi-Buy Quote:',
      error
    );


    return [];
  }
}



/*
============================================================
ESCAPE HTML
============================================================
*/


function escapeDrawerHtml(
  value
) {
  return String(
    value || ''
  )
    .replaceAll(
      '&',
      '&amp;'
    )
    .replaceAll(
      '<',
      '&lt;'
    )
    .replaceAll(
      '>',
      '&gt;'
    )
    .replaceAll(
      '"',
      '&quot;'
    )
    .replaceAll(
      "'",
      '&#039;'
    );
}



/*
============================================================
PRICE REQUEST OR MULTI-BUY?
============================================================

PRICE REQUEST
-------------
Exactly one distinct product which is POA.

Quantity can still be 2, 3, 4 etc.

MULTI-BUY
---------
More than one distinct product, or a normal
priced product added to the Multi-Buy Quote.
============================================================
*/


function isDrawerPriceRequest(
  items
) {
  if (
    items.length !== 1
  ) {
    return false;
  }


  const item =
    items[0];


  return (
    item.priceRequest === true ||
    Number(
      item.price || 0
    ) === 0
  );
}



/*
============================================================
UPDATE DRAWER WORDING
============================================================
*/


function updateMultiBuyDrawerWording(
  drawer,
  items
) {
  const priceRequest =
    isDrawerPriceRequest(
      items
    );


  const eyebrow =
    drawer.querySelector(
      '[data-quote-drawer-eyebrow]'
    );


  const title =
    drawer.querySelector(
      '[data-quote-drawer-title]'
    );


  const countText =
    drawer.querySelector(
      '[data-quote-drawer-count-text]'
    );


  const message =
    drawer.querySelector(
      '[data-quote-drawer-message]'
    );


  const viewButton =
    drawer.querySelector(
      '[data-quote-drawer-view]'
    );


  const continueButton =
    drawer.querySelector(
      '.multi-buy-quote-drawer__continue'
    );


  const totalQuantity =
    items.reduce(
      (total, item) => {
        return (
          total +
          Number(
            item.quantity || 1
          )
        );
      },
      0
    );


  /*
  ============================================================
  PRICE REQUEST
  ============================================================
  */

  if (priceRequest) {

    if (eyebrow) {
      eyebrow.textContent =
        'Price & Availability';
    }


    if (title) {
      title.textContent =
        'Request Your Price';
    }


    if (countText) {
      countText.innerHTML =
        `
          <span data-quote-drawer-count>${totalQuantity}</span>
          ${
            totalQuantity === 1
              ? 'product requested'
              : 'products requested'
          }
        `;
    }


    if (message) {
      message.innerHTML =
        `
          <strong
            style="
              display:block;
              margin-bottom:5px;
              color:#111111;
              font-size:14px;
              font-weight:600;
              line-height:1.4;
            "
          >
            Get our latest price
          </strong>

          <p
            style="
              margin:0;
              color:#6f6f69;
              font-size:13px;
              line-height:1.55;
            "
          >
            We'll confirm our latest price and
            availability for this appliance.
          </p>


          <div
            style="
              margin-top:17px;
              padding-top:16px;
              border-top:1px solid rgba(17,17,17,0.08);
            "
          >

            <div
              style="
                margin-bottom:7px;
                color:#59624a;
                font-size:10px;
                font-weight:650;
                line-height:1.3;
                letter-spacing:0.11em;
                text-transform:uppercase;
              "
            >
              Build a Multi-Buy Quote →
            </div>


            <strong
              style="
                display:block;
                margin-bottom:5px;
                color:#111111;
                font-size:13px;
                font-weight:600;
                line-height:1.4;
              "
            >
              Buying more than one appliance?
            </strong>


            <p
              style="
                margin:0;
                color:#6f6f69;
                font-size:13px;
                line-height:1.55;
              "
            >
              Add the other appliances you're considering
              and our specialists will review your complete
              package to provide our best personalised
              Multi-Buy price.
            </p>

          </div>
        `;
    }


    if (continueButton) {
      continueButton.textContent =
        'Add More Appliances';
    }


    if (viewButton) {
      viewButton.textContent =
        'View Price Request';
    }


    return;
  }


  /*
  ============================================================
  MULTI-BUY
  ============================================================
  */

  if (eyebrow) {

    if (
      items.length > 1
    ) {
      eyebrow.textContent =
        `${items.length} Appliances Selected`;

    } else {
      eyebrow.textContent =
        'Multi-Buy';
    }

  }


  if (title) {
    title.textContent =
      'Your Multi-Buy Quote';
  }


  if (countText) {
    countText.innerHTML =
      `
        <span data-quote-drawer-count>${totalQuantity}</span>
        ${
          totalQuantity === 1
            ? 'product selected'
            : 'products selected'
        }
      `;
  }


  if (message) {
    message.innerHTML =
      `
        <strong
          style="
            display:block;
            margin-bottom:5px;
            color:#111111;
            font-size:14px;
            font-weight:600;
            line-height:1.4;
          "
        >
          Personalised package pricing
        </strong>

        <p
          style="
            margin:0;
            color:#6f6f69;
            font-size:13px;
            line-height:1.55;
          "
        >
          We'll review your selected appliances
          together and prepare our best personalised
          Multi-Buy price across your complete package.
        </p>
      `;
  }


  if (continueButton) {
    continueButton.textContent =
      'Continue Shopping';
  }


  if (viewButton) {
    viewButton.textContent =
      'View Multi-Buy Quote';
  }
}



/*
============================================================
RENDER DRAWER
============================================================
*/


function renderMultiBuyQuoteDrawer() {
  const drawer =
    document.getElementById(
      'MultiBuyQuoteDrawer'
    );


  if (!drawer) {
    return;
  }


  const itemsContainer =
    drawer.querySelector(
      '[data-quote-drawer-items]'
    );


  const emptyState =
    drawer.querySelector(
      '[data-quote-drawer-empty]'
    );


  const footer =
    drawer.querySelector(
      '[data-quote-drawer-footer]'
    );


  const countElement =
    drawer.querySelector(
      '[data-quote-drawer-count]'
    );


  const totalElement =
    drawer.querySelector(
      '[data-quote-drawer-total]'
    );


  const totalLabel =
    drawer.querySelector(
      '[data-quote-drawer-total-label]'
    );


  const poaNote =
    drawer.querySelector(
      '[data-quote-drawer-poa-note]'
    );


  const items =
    getMultiBuyQuoteItems();


  /*
  Dynamic Price Request / Multi-Buy copy.
  */

  updateMultiBuyDrawerWording(
    drawer,
    items
  );


  const totalQuantity =
    items.reduce(
      (total, item) => {
        return (
          total +
          Number(
            item.quantity || 1
          )
        );
      },
      0
    );


  /*
  Keep original count hook updated.
  */

  if (countElement) {
    countElement.textContent =
      String(
        totalQuantity
      );
  }


  /*
  ============================================================
  EMPTY STATE
  ============================================================
  */

  if (!itemsContainer) {
    return;
  }


  if (!items.length) {

    itemsContainer.innerHTML =
      '';


    if (emptyState) {
      emptyState.hidden =
        false;
    }


    if (footer) {
      footer.hidden =
        true;
    }


    return;
  }


  if (emptyState) {
    emptyState.hidden =
      true;
  }


  if (footer) {
    footer.hidden =
      false;
  }


  /*
  ============================================================
  TOTALS
  ============================================================
  */

  let pricedTotal =
    0;


  let hasPoa =
    false;


  items.forEach(
    item => {

      const price =
        Number(
          item.price || 0
        );


      const quantity =
        Number(
          item.quantity || 1
        );


      if (price > 0) {

        pricedTotal +=
          price *
          quantity;

      } else {

        hasPoa =
          true;

      }

    }
  );


  /*
  Single POA Price Request.
  */

  if (
    isDrawerPriceRequest(
      items
    )
  ) {

    if (totalLabel) {
      totalLabel.textContent =
        'Price';
    }


    if (totalElement) {
      totalElement.textContent =
        'Price on request';
    }


    if (poaNote) {
      poaNote.hidden =
        true;
    }


  /*
  Multi-Buy containing POA.
  */

  } else if (hasPoa) {

    if (totalLabel) {
      totalLabel.textContent =
        'Website total';
    }


    if (totalElement) {

      if (pricedTotal > 0) {

        totalElement.textContent =
          `${formatQuoteMoney(
            pricedTotal
          )} + POA`;

      } else {

        totalElement.textContent =
          'Price on request';

      }

    }


    if (poaNote) {
      poaNote.hidden =
        false;
    }


  /*
  All products priced.
  */

  } else {

    if (totalLabel) {
      totalLabel.textContent =
        'Website total';
    }


    if (totalElement) {
      totalElement.textContent =
        formatQuoteMoney(
          pricedTotal
        );
    }


    if (poaNote) {
      poaNote.hidden =
        true;
    }

  }


  /*
  ============================================================
  PRODUCT LINES
  ============================================================
  */

  itemsContainer.innerHTML =
    items
      .map(
        item => {

          const quantity =
            Number(
              item.quantity || 1
            );


          const productUrl =
            escapeDrawerHtml(
              item.url || '#'
            );


          const image =
            escapeDrawerHtml(
              item.image || ''
            );


          const title =
            escapeDrawerHtml(
              item.title ||
              'Product'
            );


          const sku =
            escapeDrawerHtml(
              item.sku || ''
            );


          const price =
            Number(
              item.price || 0
            );


          const imageHtml =
            image
              ? `
                <a href="${productUrl}">
                  <img
                    class="multi-buy-quote-drawer__image"
                    src="${image}"
                    alt=""
                    loading="lazy"
                  >
                </a>
              `
              : `
                <div
                  class="multi-buy-quote-drawer__image"
                ></div>
              `;


          const skuHtml =
            sku
              ? `
                <p
                  class="multi-buy-quote-drawer__item-sku"
                >
                  Model: ${sku}
                </p>
              `
              : '';


          const priceText =
  item.priceRequest === true
    ? 'Price on request'
    : price > 0
      ? formatQuoteMoney(
          price
        )
      : 'Price on request';


          return `
            <div
              class="multi-buy-quote-drawer__item"
            >

              ${imageHtml}

              <div>

                <a
                  href="${productUrl}"
                  class="multi-buy-quote-drawer__item-title"
                >
                  ${title}
                </a>

                ${skuHtml}

                <p
                  class="multi-buy-quote-drawer__item-price"
                >
                  ${priceText}
                </p>

              </div>


              <div
                class="multi-buy-quote-drawer__item-quantity"
              >
                ×${quantity}
              </div>

            </div>
          `;

        }
      )
      .join('');
}



/*
============================================================
OPEN DRAWER
============================================================

UNCHANGED FROM THE ORIGINAL WORKING FILE.
============================================================
*/


function openMultiBuyQuoteDrawer() {
  const drawer =
    document.getElementById(
      'MultiBuyQuoteDrawer'
    );


  if (!drawer) {
    return;
  }


  renderMultiBuyQuoteDrawer();


  drawer.classList.add(
    'is-open'
  );


  drawer.setAttribute(
    'aria-hidden',
    'false'
  );


  document.body.classList.add(
    'quote-drawer-open'
  );
}



/*
============================================================
CLOSE DRAWER
============================================================

UNCHANGED FROM THE ORIGINAL WORKING FILE.
============================================================
*/


function closeMultiBuyQuoteDrawer() {
  const drawer =
    document.getElementById(
      'MultiBuyQuoteDrawer'
    );


  if (!drawer) {
    return;
  }


  drawer.classList.remove(
    'is-open'
  );


  drawer.setAttribute(
    'aria-hidden',
    'true'
  );


  document.body.classList.remove(
    'quote-drawer-open'
  );
}



/*
============================================================
DRAWER CLICK EVENTS
============================================================

UNCHANGED FROM THE ORIGINAL WORKING FILE.
============================================================
*/


document.addEventListener(
  'click',
  event => {

    const closeButton =
      event.target.closest(
        '[data-quote-drawer-close]'
      );


    if (closeButton) {

      closeMultiBuyQuoteDrawer();

      return;

    }


    const quoteLink =
      event.target.closest(
        '[data-quote-link]'
      );


    if (
      quoteLink &&
      quoteLink.dataset
        .quoteDrawerTrigger !==
        'false'
    ) {

      const drawer =
        document.getElementById(
          'MultiBuyQuoteDrawer'
        );


      if (drawer) {

        event.preventDefault();

        openMultiBuyQuoteDrawer();

      }

    }

  }
);



/*
============================================================
ESC KEY
============================================================
*/


document.addEventListener(
  'keydown',
  event => {

    if (
      event.key === 'Escape'
    ) {

      closeMultiBuyQuoteDrawer();

    }

  }
);



/*
============================================================
QUOTE UPDATED
============================================================
*/


document.addEventListener(
  'multi-buy-quote:updated',
  () => {

    renderMultiBuyQuoteDrawer();

  }
);



/*
============================================================
PUBLIC DRAWER API
============================================================
*/


window.MultiBuyQuoteDrawer = {
  open:
    openMultiBuyQuoteDrawer,

  close:
    closeMultiBuyQuoteDrawer,

  render:
    renderMultiBuyQuoteDrawer
};



/*
============================================================
INITIAL DRAWER RENDER
============================================================
*/


renderMultiBuyQuoteDrawer();



/*
============================================================
TINKER FILTER / SORT SUPPORT
============================================================

This only refreshes the conditional POA buttons
when Tinker replaces product cards.

It does not alter the drawer.
============================================================
*/


let quoteRefreshQueued =
  false;


function queueQuoteRefresh() {

  if (
    quoteRefreshQueued
  ) {
    return;
  }


  quoteRefreshQueued =
    true;


  window.requestAnimationFrame(
    () => {

      quoteRefreshQueued =
        false;


      if (
        window.MultiBuyQuote &&
        typeof
          window.MultiBuyQuote.refresh ===
          'function'
      ) {

        window.MultiBuyQuote.refresh();

      }

    }
  );
}



const quoteObserver =
  new MutationObserver(
    mutations => {

      for (
        const mutation of mutations
      ) {

        if (
          mutation.type ===
            'childList' &&
          mutation.addedNodes.length
        ) {

          queueQuoteRefresh();

          break;

        }

      }

    }
  );


if (
  document.body
) {

  quoteObserver.observe(
    document.body,
    {
      childList:
        true,

      subtree:
        true
    }
  );

}